import React, {useMemo, useImperativeHandle, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe, Text} from '@chakra-ui/react3'

import {
    hasValidAsset,
    hasValidImageSrc,
    hasValidString,
    isVideoAsset,
    isFullVideo,
    isDarkenOff as isDarkenOffUtil,
    normalizeDarken
} from '../../../utils/utils'
import Video from '../video'
import VideoControl from '../video-control'
import Image from '../image'
import useVideoControl from '../../../hooks/useVideoControl'
import usePlayableVideo from '../../../hooks/usePlayableVideo'
import {assetSlotRecipe, AUTOPLAY_BUTTON_VARIANT_MAP} from './index.recipe'

/**
 * Asset component to display image or video
 *
 * @component
 * @param {object} [asset] - Optional asset object
 * @param {object|function} [videoRef] - Ref object or callback for the video element
 * @param {string} [darken] - Darken overlay intensity
 * @returns {JSX.Element|null} Rendered asset or null if no asset provided
 */

const Asset = ({asset, videoRef, loading, widths, objectFit, variant = 'default', darken}) => {
    const ref = useRef(null)
    const fullVideo = isFullVideo(asset)
    const [playable, onVideoError] = usePlayableVideo(asset)
    const [isPlaying, onVideoToggle, controls] = useVideoControl(asset?.autoplay, ref, {
        isFullVideo: fullVideo
    })

    // Keep `isPlaying` in sync with the actual video state since `onVideoToggle` flips state.
    // This preserves the existing imperative `play()`/`pause()` consumer API without changes.
    useImperativeHandle(videoRef, () => ({
        play: controls.play,
        pause: controls.pause
    }))

    const hasDarkenProp = hasValidString(darken)
    const isDarkenOff = hasDarkenProp ? isDarkenOffUtil(darken) : false
    const recipeDarken = hasDarkenProp ? normalizeDarken(darken) : '40%'

    const recipe = useSlotRecipe({recipe: assetSlotRecipe})
    const styles = useMemo(
        () => recipe({variant, darken: recipeDarken}),
        [recipe, variant, recipeDarken]
    )

    if (!hasValidAsset(asset)) return null

    if (isVideoAsset(asset)) {
        const {videoName, duration} = asset
        const {overlay} = controls

        const videoControlVariant = isDarkenOff
            ? AUTOPLAY_BUTTON_VARIANT_MAP.withoutDarken
            : asset?.showVideoInfo
            ? 'mediaFilled'
            : AUTOPLAY_BUTTON_VARIANT_MAP.withDarken

        return (
            <Box css={styles.container}>
                <Video
                    ref={ref}
                    {...asset}
                    loading={loading}
                    widths={widths}
                    objectFit={objectFit}
                    onError={onVideoError}
                    {...(fullVideo && {
                        videoType: 'video',
                        onPlay: controls.onPlay
                    })}
                />
                {/* Darken layer: always visible for animation when darken is applied, only on poster state for full video */}
                {overlay.showDarken && !isDarkenOff && <Box css={styles.bottomGradient}></Box>}
                {/* Animation mode: custom control */}
                {overlay.showAnimationControl && playable && (
                    <Box css={styles.videoControlBox}>
                        <VideoControl
                            isPlaying={isPlaying}
                            onToggle={onVideoToggle}
                            variant={videoControlVariant}
                            {...((variant === 'gallery' || variant === 'teaser') && {size: 'md'})}
                        />
                        {videoName && (
                            <Text textStyle="body.small" color="text.on-media.primary">
                                {videoName}
                            </Text>
                        )}
                        {duration && (
                            <Text textStyle="body.small" color="text.on-media.primary">
                                {duration}
                            </Text>
                        )}
                    </Box>
                )}
                {/* Full video mode: centered play button on poster state */}
                {overlay.showPosterOverlay && playable && (
                    <Box css={styles.fullVideoPlayButton}>
                        <VideoControl
                            isPlaying={false}
                            onToggle={() => {
                                controls.play()
                            }}
                            variant="mediaFilled"
                            size="lg"
                        />
                    </Box>
                )}
            </Box>
        )
    }

    if (hasValidImageSrc(asset)) {
        return (
            <Box css={styles.container}>
                <Image
                    {...asset}
                    loading={loading}
                    objectFit={objectFit}
                    fetchpriority={loading === 'eager' ? 'high' : undefined}
                    widths={widths}
                    // fetchpriority="high" optimizes LCP (Largest Contentful Paint) by prioritizing
                    // hero image download over other resources. Combined with loading="eager" and
                    // preload, this ensures fastest possible above-the-fold render.
                />
            </Box>
        )
    }
}

Asset.propTypes = {
    asset: PropTypes.object,
    videoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    loading: PropTypes.string,
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    objectFit: PropTypes.string,
    variant: PropTypes.string,
    darken: PropTypes.string
}

export default Asset
